import { createAction, createAsyncThunk, createEntityAdapter, createReducer } from "@reduxjs/toolkit";
import { ApplicationHistoryEntityType, IApplicationHistoryEntity } from "models/Application.model";
import { FetchPaginatedApplicationHistoryArgs } from "services/application/ApplicationService.types";
import { serviceContainer } from "services/serviceContainer";
import { RootState } from "store/types";
import { createDeepEqualSelector } from "store/utils";

const applicationHistoryEntityAdapter = createEntityAdapter<IApplicationHistoryEntity>({
  selectId: (entity) => entity.id,
  sortComparer: (a, b) => {
    return b.id.localeCompare(a.id);
  },
});

export const initialState = applicationHistoryEntityAdapter.getInitialState();

export const loadApplicationHistory = createAction<IApplicationHistoryEntity[]>(
  "domainData/application/loadApplicationHistory"
);

export const fetchPaginatedApplicationHistory = createAsyncThunk(
  "domainData/application/fetchPaginatedApplicationHistory",
  async (args: FetchPaginatedApplicationHistoryArgs) => {
    const applicationService = serviceContainer.cradle.applicationService;
    const applicationHistory = await applicationService.fetchPaginatedApplicationHistory(args);
    return applicationHistory;
  }
);

export const applicationHistoryReducer = createReducer<typeof initialState>(initialState, (builder) =>
  builder
    .addCase(loadApplicationHistory, applicationHistoryEntityAdapter.setAll)
    .addCase(fetchPaginatedApplicationHistory.fulfilled, applicationHistoryEntityAdapter.upsertMany)
);

export const {
  selectById: selectApplicationHistoryEntityById,
  selectAll: selectAllApplicationHistoryEntities,
} = applicationHistoryEntityAdapter.getSelectors((state: RootState) => state.domainData.applicationHistory);

export const selectApplicationHistoryByApplicationId = createDeepEqualSelector(
  [
    (_: RootState, args: { applicationId: number; entityId: number; entity: ApplicationHistoryEntityType }) => args,
    selectAllApplicationHistoryEntities,
  ],
  (args, applicationHistory) => {
    return applicationHistory.filter(
      (item) => item.applicationId === args.applicationId && item.eventData.entityId === args.entityId
    );
  }
);
